import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { livestreamService } from '../../services/livestream';
import { productService } from '../../services/products';
import { chatService } from '../../services/chat';
import Alert from '../../components/Common/Alert';
import { ImageUtils } from '../../utils/imageUtils';

// Utility functions moved outside the component to avoid hoisting issues and redundant re-renders
const formatCurrencyValue = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(amount);
};

const formatDateTimeValue = (dateString) => {
  if (!dateString) return 'Not available';
  return new Date(dateString).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDurationValue = (minutes) => {
  if (!minutes) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const calculateDurationMinutes = (livestream) => {
  if (!livestream || !livestream.actual_start_time || !livestream.end_time) return 0;
  const start = new Date(livestream.actual_start_time);
  const end = new Date(livestream.end_time);
  return Math.round((end - start) / (1000 * 60)); // in minutes
};

const LivestreamDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livestream, setLivestream] = useState(null);
  const [products, setProducts] = useState([]);
  const [biddings, setBiddings] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');


  const loadLivestreamDetails = useCallback(async () => {
    try {
      setLoading(true);

      // Load livestream basic data
      const livestreamData = await livestreamService.getLivestream(id);
      setLivestream(livestreamData);

      // Load products for this livestream - use the products array from livestream data
      if (livestreamData.products && Array.isArray(livestreamData.products)) {
        // If products have full data, use them directly
        if (livestreamData.products.length > 0 && livestreamData.products[0].name) {
          setProducts(livestreamData.products);
        } else {
          // If products only have IDs, fetch full product details
          try {
            const productIds = livestreamData.products.map(p => typeof p === 'object' ? p.id : p);
            const productPromises = productIds.map(id => productService.getProduct(id));
            const fullProducts = await Promise.all(productPromises);
            setProducts(fullProducts.filter(p => p)); // Filter out any failed requests
          } catch (detailError) {
            console.warn('Could not load detailed product information:', detailError);
            setProducts(livestreamData.products);
          }
        }
      } else {
        // If products are not included, try to fetch them separately
        try {
          const productsData = await productService.getProductsForLivestream(id);
          setProducts(productsData.products || productsData || []);
        } catch (productError) {
          console.warn('Could not load products for livestream:', productError);
          setProducts([]);
        }
      }

      // Load biddings data
      const biddingsResponse = await livestreamService.getBiddings({ livestream: id });
      const biddingsData = biddingsResponse.results || biddingsResponse || [];
      setBiddings(biddingsData);

      // Load chat messages (recent ones)
      try {
        const chatData = await chatService.getChatMessages(id, { limit: 50 });
        setChatMessages(chatData.results || chatData || []);
      } catch (chatError) {
        console.warn('Could not load chat messages:', chatError);
      }

    } catch (error) {
      console.error('Error loading livestream details:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load livestream details. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLivestreamDetails();
  }, [loadLivestreamDetails]);



  const handleEditLivestream = () => {
    navigate(`/livestreams/${id}/edit`);
  };

  const handleViewAnalytics = () => {
    navigate(`/livestreams/${id}/analytics`);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">Loading Details...</p>
        </div>
      </div>
    );
  }

  if (!livestream) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center p-6">
        <div className="bg-dark-800/50 backdrop-blur-xl p-8 rounded-2xl border border-transparent text-center max-w-sm">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="bi bi-exclamation-triangle text-red-500 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Not Found</h2>
          <p className="text-gray-400 text-xs mb-6">This session doesn't exist or is unavailable.</p>
          <button
            onClick={() => navigate('/livestreams')}
            className="w-full py-2.5 bg-red-500 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-red-600 transition-all"
          >
            Back to List
          </button>
        </div>
      </div>
    );
  }

  const duration = calculateDurationMinutes(livestream);

  return (
    <div className="min-h-screen bg-dark-950 text-gray-100 p-4 md:p-6 lg:p-8">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center border border-red-500/20 shadow-lg shadow-red-500/5">
            <i className="bi bi-info-circle-fill text-red-500 text-xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-0.5">
              <span>Livestreams</span>
              <i className="bi bi-chevron-right text-xs"></i>
              <span className="text-gray-400">Details</span>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Livestream Details</h1>
            <p className="text-gray-500 text-xs font-medium">Complete session data for "{livestream.title}"</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleViewAnalytics}
            className="flex items-center gap-2 px-4 py-2 bg-dark-800/50 backdrop-blur-md border border-transparent text-white rounded-xl hover:bg-dark-700/50 transition-all font-bold text-xs shadow-lg"
          >
            <i className="bi bi-graph-up-arrow"></i>
            View Analytics
          </button>

          <button
            onClick={handleEditLivestream}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all font-bold text-xs shadow-lg shadow-red-500/20"
          >
            <i className="bi bi-pencil-square"></i>
            Edit Session
          </button>

          <button
            onClick={() => navigate('/livestreams')}
            className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-all font-bold text-xs"
          >
            <i className="bi bi-arrow-left"></i>
            Back
          </button>
        </div>
      </div>

      {alert && (
        <div className="mb-8">
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={() => setAlert(null)}
          />
        </div>
      )}

      {/* Hero Showcase Card */}
      <div className="bg-dark-800/30 backdrop-blur-2xl rounded-3xl border border-transparent overflow-hidden mb-6 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.5)]">
        <div className="flex flex-col lg:flex-row min-h-[350px]">
          {/* Visual Side */}
          <div className="lg:w-[35%] relative overflow-hidden group">
            {livestream.thumbnail ? (
              <img
                src={ImageUtils.getFullImageUrl(livestream.thumbnail)}
                alt={livestream.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-dark-900 flex items-center justify-center">
                <i className="bi bi-camera-video text-gray-700 text-4xl"></i>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/20 to-transparent"></div>

            {livestream.status === 'ended' && (
              <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/10 backdrop-blur-xl px-3 py-1.5 rounded-full border border-transparent shadow-2xl">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse shadow-[0_0_8px_rgba(255,255,255,0.8)]"></div>
                <span className="text-white text-xs font-bold tracking-[0.15em] uppercase">Rewatch Available</span>
              </div>
            )}
          </div>

          {/* Identity Side */}
          <div className="lg:w-[65%] p-6 lg:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tighter leading-tight">
                {livestream.title}
              </h2>
              <div className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-widest border border-transparent backdrop-blur-md ${livestream.status === 'live' ? 'bg-red-500/20 text-red-500 border-red-500/20 animate-pulse' :
                  livestream.status === 'ended' ? 'bg-white/5 text-gray-400' :
                    'bg-primary-500/10 text-primary-500 border-primary-500/20'
                }`}>
                {livestream.status}
              </div>
            </div>

            <p className="text-gray-400 text-sm font-medium mb-6 leading-relaxed max-w-2xl line-clamp-2">
              {livestream.description}
            </p>

            <div className="h-px bg-white/5 w-full mb-6"></div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
              <div className="space-y-1">
                <span className="text-gray-500 text-xs uppercase font-bold tracking-widest block">Category</span>
                <span className="text-red-500 text-base font-bold tracking-tight">{livestream.category_name || 'N/A'}</span>
              </div>

              <div className="space-y-1">
                <span className="text-gray-500 text-xs uppercase font-bold tracking-widest block">End Time</span>
                <span className="text-white text-base font-bold tracking-tight">
                  {livestream.end_time ? new Date(livestream.end_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                </span>
              </div>

              <div className="space-y-1">
                <span className="text-gray-500 text-xs uppercase font-bold tracking-widest block">Scheduled</span>
                <span className="text-gray-400/50 text-base font-bold tracking-tight italic">
                  {livestream.scheduled_start_time ? new Date(livestream.scheduled_start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                </span>
              </div>

              <div className="space-y-1">
                <span className="text-gray-500 text-xs uppercase font-bold tracking-widest block">Duration</span>
                <span className="text-red-500 text-base font-bold tracking-tight">{formatDurationValue(duration)}</span>
              </div>

              <div className="space-y-1">
                <span className="text-gray-500 text-xs uppercase font-bold tracking-widest block">Actual Start</span>
                <span className="text-white text-base font-bold tracking-tight">
                  {livestream.actual_start_time ? new Date(livestream.actual_start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                </span>
              </div>

              <div className="space-y-1">
                <span className="text-gray-500 text-xs uppercase font-bold tracking-widest block">Credits</span>
                <span className="text-white text-base font-bold tracking-tight">{livestream.credits_consumed || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Quick View */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Viewers', value: livestream.viewer_count || 0, icon: 'bi-people-fill' },
          { label: 'Products', value: products.length, icon: 'bi-box-seam-fill' },
          { label: 'Biddings', value: biddings.length, icon: 'bi-hammer' },
          { label: 'Messages', value: chatMessages.length, icon: 'bi-chat-left-text-fill' }
        ].map((stat, i) => (
          <div key={i} className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent p-4 flex flex-col items-center justify-center text-center group hover:bg-dark-800/60 transition-all hover:scale-[1.02] shadow-lg">
            <div className="mb-3 w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform border border-red-500/5">
              <i className={`bi ${stat.icon} text-red-500 text-xl`}></i>
            </div>
            <div className="text-2xl font-bold text-white mb-0.5 tabular-nums tracking-tighter">{stat.value}</div>
            <div className="text-gray-500 text-xs uppercase font-bold tracking-[0.1em]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Detailed Content Tabs */}
      <div className="bg-dark-800/20 backdrop-blur-2xl rounded-3xl border border-transparent overflow-hidden shadow-xl">
        <div className="flex border-b border-transparent overflow-x-auto no-scrollbar bg-dark-800/30">
          {[
            { id: 'overview', label: 'Overview', icon: 'bi-grid-fill' },
            { id: 'products', label: `Products (${products.length})`, icon: 'bi-box-seam-fill' },
            { id: 'biddings', label: `Biddings (${biddings.length})`, icon: 'bi-hammer' },
            { id: 'chat', label: `Chat (${chatMessages.length})`, icon: 'bi-chat-left-text-fill' },
            { id: 'analytics', label: 'Analytics', icon: 'bi-bar-chart-fill' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-all outline-none whitespace-nowrap border-b-2 ${activeTab === tab.id
                  ? 'text-red-500 border-red-500 bg-red-500/5'
                  : 'text-gray-500 border-transparent hover:text-white hover:bg-white/5'
                }`}
            >
              <i className={`bi ${tab.icon} text-sm ${activeTab === tab.id ? 'text-red-500' : 'text-gray-600'}`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-0">
          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <div className="p-6 lg:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h6 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
                    <span className="w-6 h-px bg-gray-800"></span>
                    Technical Details
                  </h6>
                  <div className="space-y-4">
                    <div className="group">
                      <div className="flex justify-between items-center py-3 border-b border-transparent group-hover:bg-white/[0.02] px-3 rounded-lg transition-all">
                        <span className="text-gray-400 font-bold text-xs">Session ID</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-mono text-xs bg-dark-900 px-2 py-1 rounded border border-transparent">{livestream.id.substring(0, 18)}...</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(livestream.id);
                              setAlert({ type: 'success', message: 'ID copied' });
                            }}
                            className="text-gray-500 hover:text-white transition-colors"
                          >
                            <i className="bi bi-copy text-xs"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <div className="flex justify-between items-center py-3 border-b border-transparent group-hover:bg-white/[0.02] px-3 rounded-lg transition-all">
                        <span className="text-gray-400 font-bold text-xs">Agora Channel</span>
                        <span className="text-white font-mono text-xs bg-red-500/5 text-red-400 px-2 py-1 rounded border border-red-500/10">
                          {livestream.agora_channel_name || 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className="group">
                      <div className="flex justify-between items-center py-3 border-b border-transparent group-hover:bg-white/[0.02] px-3 rounded-lg transition-all">
                        <span className="text-gray-400 font-bold text-xs">Created At</span>
                        <span className="text-white font-medium text-xs">{formatDateTimeValue(livestream.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h6 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
                    <span className="w-6 h-px bg-gray-800"></span>
                    Performance Summary
                  </h6>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-dark-900/50 rounded-2xl border border-transparent group hover:bg-dark-900 transition-all text-center">
                      <div className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2">Success</div>
                      <div className="text-xl font-bold text-green-500 mb-1">
                        {biddings.length > 0 ?
                          `${Math.round((biddings.filter(b => b.winner).length / biddings.length) * 100)}%` :
                          'N/A'
                        }
                      </div>
                      <div className="w-8 h-0.5 bg-green-500/20 mx-auto rounded-full"></div>
                    </div>

                    <div className="p-4 bg-dark-900/50 rounded-2xl border border-transparent group hover:bg-dark-900 transition-all text-center">
                      <div className="text-xl font-bold text-red-500 mb-1 tracking-tighter">
                        {formatCurrencyValue(
                          biddings
                            .filter(b => b.winner && b.current_highest_bid)
                            .reduce((sum, b) => sum + parseFloat(b.current_highest_bid || 0), 0)
                        ).replace('.00', '')}
                      </div>
                      <div className="w-8 h-0.5 bg-red-500/20 mx-auto rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Products Tab Content */}
          {activeTab === 'products' && (
            <div className="p-6 lg:p-8">
              {products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {products.map((product) => (
                    <div key={product.id} className="group relative">
                      <div className="bg-dark-900/40 backdrop-blur-md rounded-2xl overflow-hidden border border-transparent transition-all duration-500 hover:bg-dark-900/60 hover:scale-[1.02] hover:shadow-xl flex flex-col h-full">
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={ImageUtils.getProductImageUrl(product)}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            alt={product.name}
                            onError={(e) => { e.target.src = ImageUtils.getPlaceholderImage(); }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent opacity-60"></div>
                          <div className="absolute top-2 right-2 flex flex-col gap-1">
                            {(product.available_quantity ?? product.quantity ?? 0) === 0 && (
                              <span className="px-2 py-1 bg-red-500/90 backdrop-blur-md text-white text-xs font-bold uppercase tracking-widest rounded shadow-xl">
                                Out of Stock
                              </span>
                            )}
                          </div>
                          <div className="absolute bottom-2 left-2">
                            <span className="px-2 py-0.5 bg-white/10 backdrop-blur-md text-white text-xs font-bold uppercase tracking-widest rounded border border-transparent">
                              {product.category_name || 'Item'}
                            </span>
                          </div>
                        </div>

                        <div className="p-3 flex-grow flex flex-col">
                          <h6 className="text-sm font-bold text-white mb-1 tracking-tight group-hover:text-red-500 transition-colors line-clamp-1">
                            {product.name}
                          </h6>

                          <div className="mt-auto">
                            <div className="flex justify-between items-end mb-2">
                              <div className="flex flex-col">
                                <span className="text-2xl font-bold text-white tracking-tighter text-lg">
                                  ₹{formatCurrencyValue(parseFloat(product.base_price || product.price || 0))}
                                </span>
                              </div>
                              <div className={`px-2 py-1 rounded-lg flex items-center gap-1 border ${(product.available_quantity ?? product.quantity ?? 0) > 10 ? 'bg-green-500/10 border-green-500/10 text-green-500' :
                                  'bg-yellow-500/10 border-yellow-500/10 text-yellow-500'
                                }`}>
                                <i className="bi bi-layers-half text-xs"></i>
                                <span className="text-xs font-bold uppercase tracking-widest">{product.available_quantity ?? product.quantity ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white/[0.02] rounded-3xl border border-dashed border-transparent">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-box-seam text-gray-600 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-400 mb-1">No Products</h3>
                  <p className="text-gray-600 text-xs max-w-xs mx-auto">This session has no products attached.</p>
                </div>
              )}
            </div>
          )}

          {/* Biddings Tab Content */}
          {activeTab === 'biddings' && (
            <div className="p-6 lg:p-8">
              {biddings.length > 0 ? (
                <div className="overflow-hidden rounded-2xl border border-transparent bg-dark-900/40 shadow-xl">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-dark-900 text-xs uppercase text-gray-500 font-bold tracking-[0.15em] border-b border-transparent">
                      <tr>
                        <th className="px-6 py-4">Product</th>
                        <th className="px-4 py-4">Start</th>
                        <th className="px-4 py-4">Final</th>
                        <th className="px-4 py-4">Winner</th>
                        <th className="px-4 py-4">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {biddings.map((bidding) => (
                        <tr key={bidding.id} className="group hover:bg-white/[0.02] transition-colors">
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg overflow-hidden border border-transparent">
                                <img
                                  src={ImageUtils.getProductImageUrl(bidding.product)}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                  alt=""
                                />
                              </div>
                              <div>
                                <div className="font-bold text-white text-xs truncate max-w-[120px]">{bidding.product?.name || 'Item'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-400 font-bold">₹{parseFloat(bidding.starting_price || 0).toLocaleString()}</td>
                          <td className="px-4 py-3">
                            {bidding.current_highest_bid ? (
                              <span className="text-green-500 font-bold">₹{parseFloat(bidding.current_highest_bid).toLocaleString()}</span>
                            ) : (
                              <span className="text-gray-700">No Bids</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {bidding.winner_name ? (
                              <span className="text-white font-bold text-xs bg-red-500/10 px-2 py-1 rounded border border-red-500/10">{bidding.winner_name.split(' ')[0]}</span>
                            ) : (
                              <span className="text-gray-700 text-xs">Unsold</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full w-fit border ${bidding.status === 'active' ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-white/5 border-transparent text-gray-500'
                              }`}>
                              <div className={`w-1 h-1 rounded-full ${bidding.status === 'active' ? 'bg-red-500 animate-pulse' : 'bg-gray-600'}`}></div>
                              <span className="text-xs font-bold uppercase tracking-widest">{bidding.status}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 bg-white/[0.02] rounded-3xl border border-dashed border-transparent">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-hammer text-gray-600 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-400 mb-1">No Biddings</h3>
                  <p className="text-gray-600 text-xs max-w-xs mx-auto">No activity recorded for this session.</p>
                </div>
              )}
            </div>
          )}

          {/* Chat Tab Content */}
          {activeTab === 'chat' && (
            <div className="p-6 lg:p-8">
              {chatMessages.length > 0 ? (
                <div className="max-h-[450px] overflow-y-auto pr-4 space-y-4 custom-scrollbar">
                  {chatMessages.map((message, index) => (
                    <div key={message.id || index} className="flex gap-4 group">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-700 rounded-lg flex items-center justify-center text-white border border-transparent shadow-lg text-xs">
                          {message.sender_name?.charAt(0) || 'U'}
                        </div>
                      </div>
                      <div className="flex-grow max-w-xl">
                        <div className="flex items-center mb-1 gap-2">
                          <strong className="text-white text-xs font-bold tracking-tight">{message.sender_name || 'User'}</strong>
                          <small className="text-gray-500 text-xs font-bold uppercase tracking-widest pt-0.5">
                            {new Date(message.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </small>
                        </div>
                        <div className="bg-dark-900 border border-transparent rounded-2xl p-3 shadow-xl">
                          <div className="text-gray-300 text-xs font-medium leading-relaxed break-words">
                            {message.content || message.message}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white/[0.02] rounded-3xl border border-dashed border-transparent">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-chat-dots text-gray-600 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-400 mb-1">Empty Chat</h3>
                  <p className="text-gray-600 text-xs max-w-xs mx-auto">No messages exchanged yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab Content */}
          {activeTab === 'analytics' && (
            <div className="p-6 lg:p-8">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Revenue', value: formatCurrencyValue(biddings.filter(b => b.current_highest_bid).reduce((sum, b) => sum + (parseFloat(b.current_highest_bid) || 0), 0)).replace('.00', ''), icon: 'bi-graph-up-arrow', color: 'text-red-500' },
                  { label: 'Bids / Item', value: biddings.length > 0 ? (biddings.reduce((sum, b) => sum + (b.total_bids || 0), 0) / biddings.length).toFixed(1) : 0, icon: 'bi-lightning-charge-fill', color: 'text-yellow-500' },
                  { label: 'Sales %', value: `${biddings.length > 0 ? Math.round((biddings.filter(b => b.winner_name).length / biddings.length) * 100) : 0}%`, icon: 'bi-trophy-fill', color: 'text-red-500' },
                  { label: 'Retention', value: '84%', icon: 'bi-people-fill', color: 'text-red-500' }
                ].map((item, idx) => (
                  <div key={idx} className="p-6 bg-dark-900/40 rounded-2xl border border-transparent flex flex-col items-center justify-center text-center shadow-lg">
                    <i className={`bi ${item.icon} ${item.color} text-2xl mb-3`}></i>
                    <div className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1">{item.label}</div>
                    <div className="text-xl font-bold text-white tracking-tighter">{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-dark-900/40 rounded-2xl border border-transparent overflow-hidden shadow-xl">
                  <div className="px-6 py-4 border-b border-transparent bg-dark-900/60 flex items-center gap-2">
                    <i className="bi bi-bar-chart-fill text-red-500 text-sm"></i>
                    <h6 className="text-xs font-bold uppercase tracking-widest text-white mb-0">Engagement</h6>
                  </div>
                  <div className="p-6 space-y-4">
                    {[
                      { label: 'Retention', val: '84%', w: 'w-[84%]', c: 'bg-red-500' },
                      { label: 'Interaction', val: '62%', w: 'w-[62%]', c: 'bg-green-500' },
                      { label: 'Participation', val: '45%', w: 'w-[45%]', c: 'bg-yellow-500' }
                    ].map((row, i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="flex justify-between items-end text-xs">
                          <span className="text-gray-400 font-bold">{row.label}</span>
                          <span className="text-white font-bold">{row.val}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className={`h-full ${row.c} ${row.w} rounded-full`}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-dark-900/40 rounded-2xl border border-transparent p-6 flex flex-col justify-center shadow-xl relative overflow-hidden">
                  <h4 className="text-lg font-bold text-white mb-2 tracking-tight z-10">Rating</h4>
                  <div className="flex items-center gap-1 mb-3 z-10 text-red-500">
                    {[1, 2, 3, 4].map(s => <i key={s} className="bi bi-star-fill text-lg"></i>)}
                    <i className="bi bi-star-half text-lg"></i>
                  </div>
                  <p className="text-gray-500 text-xs font-medium leading-relaxed z-10 mb-0">
                    Performed in the <span className="text-white font-bold">top 15%</span> this month.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LivestreamDetails;





